import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { countryList, getStatesOptions } from '../../utils/locale';
import { isAddressComplete, isRequiredCountry } from '../../utils/completes';
import { countries, errors } from '../../constants';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, EntityLabel, InfoPanel, SelectField, TextField, Typography } from '@otcmarketsgroup/otcm-ui-lib';
import { MapPin, Pencil, Plus } from 'lucide-react';

const ADDRESS_1 = 'address1';
const ADDRESS_2 = 'address2';
const ADDRESS_3 = 'address3';
const CITY = 'city';
const STATE = 'state';
const ZIP = 'zip';
const COUNTRY_ID = 'countryId';

const AddressEntity = ({ title, data, isStatic, staticData, addressKey, onUpdate, isReadOnly, footNote }) => {
    const [viewFormDialog, setFormDialog] = useState(false);
    const [addressData, setAddressData] = useState({ ...data });
    const [validationError, setValidationError] = useState(false);
    const addressObj = isStatic ? staticData : data;
    const addressTitle = addressObj && `${addressObj[ADDRESS_1] ? addressObj[ADDRESS_1] : ''}${addressObj[ADDRESS_2] ? ' ' + addressObj[ADDRESS_2] : ''}
    ${addressObj[CITY] ? ' ' + addressObj[CITY] : ''}
    ${addressObj[STATE] ? ' ' + addressObj[STATE] : ''}
    ${addressObj[ZIP] ? ' ' + addressObj[ZIP] : ''}`;
    const hasAddress = addressObj && (addressObj[ADDRESS_1] || addressObj[ADDRESS_2]
        || addressObj[CITY] || addressObj[STATE] || addressObj[ZIP]) && addressTitle.length > 1 ? true : false;
    const isAdd = !data ? true : false;
    const isComplete = isAddressComplete({ ...addressData });
    const filteredStateList = getStatesOptions(addressData, COUNTRY_ID);
    useEffect(() => {
        if (!viewFormDialog) {
            setAddressData({ ...data });
        }
    }, [viewFormDialog, setAddressData, data]);

    const onValueChanged = (value, field) => {
        setAddressData(prev => ({
            ...prev,
            [field]: value || (field === COUNTRY_ID ? countries.US : ''),
            ...(field === COUNTRY_ID && prev[COUNTRY_ID] !== value ? { [STATE]: null } : {}),
        }));
    };

    const handleUpdate = () => {
        setValidationError(false);

        if (!addressData[ADDRESS_1] || !addressData[COUNTRY_ID] || !addressData[CITY] || (isRequiredCountry(addressData[COUNTRY_ID]) && !addressData[STATE]) || !addressData[ZIP]) {
            setValidationError(true);
            return;
        }

        onUpdate(addressData, addressKey);
        formDialogClose();
    };

    const formDialogClose = () => {
        setFormDialog(false);
    };

    const onEntityClick = () => {
        setFormDialog(true);
    };

    return (
        <>
            <EntityLabel
                icon={<MapPin />}
                disabled={isStatic}
                onEntityClick={() => isStatic ? undefined : onEntityClick()}
                subtitle={isStatic ? undefined : isReadOnly ? 'View' : hasAddress ? 'Edit' : 'Add'}
                isWarning={!hasAddress}
                isComplete={isComplete}
                title={hasAddress ? addressTitle : title}
            />

            <Dialog open={viewFormDialog} onClose={formDialogClose}>
                <DialogTitle title={title} onClose={formDialogClose} />
                <DialogContent>
                    <Box display='grid' gap={2} gridTemplateColumns={{ desktop: 'repeat(6, 1fr)', mobile: '1fr' }}>
                        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
                            <TextField
                                label='Address'
                                placeholder='Address 1'
                                showClearButton
                                isRequired
                                maxLength={75}
                                value={addressData?.[ADDRESS_1]}
                                readOnly={isReadOnly}
                                error={((validationError || isReadOnly) || isReadOnly) && !addressData?.[ADDRESS_1]}
                                helperText={validationError && !addressData?.[ADDRESS_1] ? 'Required' : undefined}
                                onChange={e => onValueChanged(e.target.value, ADDRESS_1)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
                            <TextField
                                placeholder='Address 2'
                                showClearButton
                                isRequired
                                maxLength={75}
                                value={addressData?.[ADDRESS_2]}
                                readOnly={isReadOnly}
                                onChange={e => onValueChanged(e.target.value, ADDRESS_2)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
                            <TextField
                                placeholder='Address 3'
                                showClearButton
                                isRequired
                                maxLength={75}
                                value={addressData?.[ADDRESS_3]}
                                readOnly={isReadOnly}
                                onChange={e => onValueChanged(e.target.value, ADDRESS_3)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 2', mobile: 'span 1' }}>
                            <SelectField
                                label="Country"
                                value={addressData?.[COUNTRY_ID]}
                                options={countryList}
                                readOnly={isReadOnly}
                                placeholder='Country'
                                error={(validationError || isReadOnly) && !addressData?.[COUNTRY_ID]}
                                onChange={e => onValueChanged(e.target.value, COUNTRY_ID)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 2', mobile: 'span 1' }}>
                            <TextField
                                label='City'
                                placeholder='City'
                                isRequired
                                showClearButton
                                maxLength={75}
                                value={addressData?.[CITY]}
                                readOnly={isReadOnly}
                                error={(validationError || isReadOnly) && !addressData?.[CITY]}
                                helperText={validationError && !addressData?.[CITY] ? 'Required' : undefined}
                                onChange={e => onValueChanged(e.target.value, CITY)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 2', mobile: 'span 1' }}>
                            <SelectField
                                label="State"
                                value={addressData?.[STATE]}
                                options={filteredStateList}
                                readOnly={isReadOnly}
                                disabled={!isRequiredCountry(addressData?.[COUNTRY_ID]) && filteredStateList?.length === 0}
                                error={(validationError || isReadOnly) && isRequiredCountry(addressData?.[COUNTRY_ID]) && !addressData?.[STATE]}
                                helperText={validationError && isRequiredCountry(addressData?.[COUNTRY_ID]) && !addressData?.[STATE] ? 'Required' : undefined}
                                onChange={e => onValueChanged(e.target.value, STATE)}
                            />
                        </Box>
                        <Box gridColumn={{ desktop: 'span 2', mobile: 'span 1' }}>
                            <TextField
                                label='Postal Code'
                                placeholder='Postal Code'
                                isRequired
                                showClearButton
                                maxLength={75}
                                value={addressData?.[ZIP]}
                                readOnly={isReadOnly}
                                error={(validationError || isReadOnly) && !addressData?.[ZIP]}
                                helperText={validationError && !addressData?.[ZIP] ? 'Required' : undefined}
                                onChange={e => onValueChanged(e.target.value, ZIP)}
                            />
                        </Box>
                        {footNote && <Box gridColumn={{ desktop: 'span 6', mobile: 'span 1' }}>
                            <Typography variant='body1'>{footNote}</Typography>
                        </Box>}
                    </Box>
                    {validationError && <Box mt={2}>
                        <InfoPanel title={errors.INCOMPLETE_DATA} isWarning />
                    </Box>}
                </DialogContent>
                {!isReadOnly && <DialogActions>
                    <Button variant='outlined' onClick={formDialogClose}>Cancel</Button>
                    <Button onClick={handleUpdate}>{isAdd ? 'Add' : 'Update'}</Button>
                </DialogActions>}
            </Dialog>
        </>
    );
};

AddressEntity.propTypes = {
    title: PropTypes.string,
    footNote: PropTypes.string,
    data: PropTypes.object,
    isStatic: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    staticData: PropTypes.object,
    addressKey: PropTypes.string,
    onUpdate: PropTypes.func
};

export default AddressEntity;
